/**
 * Species-specific FAQ data for care guide pages
 *
 * Each FAQ should be:
 * - High-value and commonly searched
 * - Concise (1-3 sentences)
 * - Tailored to the specific species
 * - Avoid duplication across species where possible
 */

export const careGuideFAQs = {
  // Oriental Fire-Bellied Toad (Bombina orientalis)
  'bombina-orientalis': [
    {
      question: "What size tank does an Oriental fire-bellied toad need?",
      answer: "A 10-gallon tank is suitable for 2-3 Oriental fire-bellied toads, though larger is always better. They need both land and water areas with a shallow water section (2-3 inches deep) that they can easily enter and exit."
    },
    {
      question: "What temperature should I keep fire-bellied toads at?",
      answer: "Oriental fire-bellied toads thrive at room temperature between 65-75°F (18-24°C). They don't require additional heating in most homes, making them one of the easiest amphibians to care for."
    },
    {
      question: "What do fire-bellied toads eat?",
      answer: "They eat small crickets, fruit flies, waxworms, and other appropriately-sized insects. Feed adults 3-4 times per week and dust food with calcium and vitamin supplements regularly."
    },
    {
      question: "Can you handle Oriental fire-bellied toads?",
      answer: "Handling should be minimal as they secrete mild toxins from their skin that can cause irritation. Always wash hands thoroughly after handling and never touch your eyes or mouth."
    },
    {
      question: "How long do fire-bellied toads live?",
      answer: "With proper care, Oriental fire-bellied toads typically live 10-15 years in captivity, with some individuals reaching 20 years."
    },
    {
      question: "Do fire-bellied toads need a filter?",
      answer: "A small aquarium filter is highly recommended to keep the water clean and reduce maintenance. Change 25-50% of the water weekly even with filtration."
    }
  ],

  // Dyeing Dart Frog / Azureus (Dendrobates tinctorius 'Azureus')
  'dendrobates-tinctorius-azureus': [
    {
      question: "What size vivarium does Dendrobates tinctorius 'Azureus' need?",
      answer: "A minimum of 18x18x18 inches (20 gallon vertical) for a pair, with larger enclosures (24x18x18 or bigger) providing better breeding success and territorial space."
    },
    {
      question: "What temperature and humidity do Azureus dart frogs require?",
      answer: "Maintain temperatures between 72-78°F (22-26°C) with a slight night drop. Humidity should stay between 80-100%, achieved through daily misting and good ventilation."
    },
    {
      question: "What do Azureus dart frogs eat?",
      answer: "They primarily eat fruit flies (Drosophila melanogaster and hydei), small crickets, and springtails. All food must be dusted with vitamin and mineral supplements, especially calcium with D3."
    },
    {
      question: "Are Dendrobates tinctorius 'Azureus' poisonous in captivity?",
      answer: "No, captive-bred dart frogs are not poisonous as they don't have access to the specific insects that provide the toxins found in wild populations."
    },
    {
      question: "How long do Azureus dart frogs live?",
      answer: "With proper care, Dendrobates tinctorius 'Azureus' typically live 10-15 years in captivity, with some reaching 20 years or more."
    },
    {
      question: "What are common mistakes with Azureus dart frogs?",
      answer: "The most common mistakes include inadequate humidity, using substrate that's too wet (causing bacterial issues), insufficient ventilation, and not providing enough hiding spots and visual barriers."
    }
  ],

  // Red-Eyed Tree Frog (Agalychnis callidryas)
  'agalychnis-callidryas': [
    {
      question: "What size enclosure does a red-eyed tree frog need?",
      answer: "A minimum of 18x18x24 inches (vertical) for one frog, with 24x18x24 or larger recommended for pairs or groups. Height is more important than floor space since they're arboreal."
    },
    {
      question: "What temperature should I maintain for red-eyed tree frogs?",
      answer: "Daytime temperatures should be 75-85°F (24-29°C) with nighttime drops to 66-77°F (19-25°C). A basking spot around 85°F is beneficial but not required."
    },
    {
      question: "What humidity level do red-eyed tree frogs need?",
      answer: "Maintain 60-80% humidity during the day, increasing to 80-100% at night. Mist heavily in the evening and provide good ventilation to prevent stagnant air."
    },
    {
      question: "What do red-eyed tree frogs eat?",
      answer: "They eat crickets, roaches, moths, and other flying insects. Adults should be fed 3-4 appropriately-sized insects every 2-3 days, with all food dusted with calcium and vitamins."
    },
    {
      question: "Can you handle red-eyed tree frogs?",
      answer: "Handling should be minimal and only when necessary. Their skin is delicate and absorbs oils and chemicals from human hands. Always use wet, clean hands if handling is required."
    },
    {
      question: "How long do red-eyed tree frogs live?",
      answer: "In captivity with proper care, red-eyed tree frogs typically live 5-8 years, though some individuals can reach 10 years or more."
    }
  ]
};

/**
 * Get FAQs for a specific species slug
 * @param {string} speciesSlug - The species slug (e.g., 'bombina-orientalis')
 * @returns {Array} Array of FAQ objects or empty array if not found
 */
export const getFAQsForSpecies = (speciesSlug) => {
  return careGuideFAQs[speciesSlug] || [];
};
